import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { Application } from '../../../interfaces/application.interface';
import { ApplicationService } from '../../../services/application.service';
import { LogMatcherContextService } from '../../../services/logmatcher-context.service';
import { SelectedCompanyService } from '../../../selected-company/selected-company.service';
import Swal from 'sweetalert2';
import { showErrorSwal } from '@asamco/utils';
import { FreshdeskHelpModalComponent, JobLogPatternDto, JobLogPatternMatchDto, JobLogPatternService, PatternStatus, UpsertJobLogPatternRequest } from '@asamco/application-link/shared';

type UiMode = 'list' | 'edit';
type DatePreset = 'last-7-days' | 'last-14-days' | 'last-30-days' | 'custom';

type UiJobLogPattern = JobLogPatternDto & {
  linkId?: number | null;
  freshdeskArticleIds?: number[];
};

type CustomerJobLogGroup = {
  companyId: number;
  companyName: string;
  jobLogs: JobLogPatternMatchDto[];
};

// ✅ keep values aligned with backend
export enum JobLogType {
  Undefined = 0,
  Success = 1,
  Fail = 2,
  AmountMismatch = 3,
  Error = 4,
  Info = 6,
}

type PagerToken =
  | { kind: 'page'; value: number; isCurrent: boolean }
  | { kind: 'gap' };

@Component({
  selector: 'appshell-link-joblog-patterns-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, FreshdeskHelpModalComponent],
  templateUrl: './link-joblog-patterns-admin.component.html',
  styleUrl: './link-joblog-patterns-admin.component.scss',
})
export class LinkJoblogPatternsAdminComponent implements OnInit {

  @ViewChild('helpModal') helpModal?: FreshdeskHelpModalComponent;

  mode: UiMode = 'list';
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>('');



  q = '';
  private readonly q$ = new BehaviorSubject<string>('');
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  private latestPatterns: UiJobLogPattern[] = [];
  private latestCategoryOptions: string[] = [];

  articleIdsText = '';
  readonly createCategoryOption = '__create_new_category__';
  categorySelection = '';
  readonly datePresetOptions: { value: DatePreset; label: string }[] = [
    { value: 'last-7-days', label: 'Last week' },
    { value: 'last-14-days', label: 'Last 2 weeks' },
    { value: 'last-30-days', label: 'Last month' },
    { value: 'custom', label: 'Custom range' },
  ];
  datePreset: DatePreset = 'last-14-days';
  fromDate = '';
  toDate = '';
  readonly expandedMatchPatternIds = new Set<number>();
  readonly loadingMatchPatternIds = new Set<number>();
  readonly patternMatches = new Map<number, JobLogPatternMatchDto[]>();
  readonly patternMatchErrors = new Map<number, string>();

  // ✅ cleaner: null = no filter, remove legacy "5 = All"
  readonly jobLogTypeOptions = [
    { value: null as number | null, label: 'All (no filter)' },
    { value: JobLogType.Error, label: 'Error' },
    { value: JobLogType.Fail, label: 'Fail' },
    { value: JobLogType.Info, label: 'Info' },
    { value: JobLogType.Success, label: 'Success' },
    { value: JobLogType.AmountMismatch, label: 'Amount mismatch' },
    { value: JobLogType.Undefined, label: 'Undefined' },
  ] as const;

  readonly statusOptions = [
    { value: 1 as PatternStatus, label: 'Active' },
    { value: 0 as PatternStatus, label: 'Inactive' },
  ] as const;

  readonly applications$: Observable<Application[]> = this.applicationService.get().pipe(
    map(apps =>
      [...(apps ?? [])].sort((a, b) =>
        this.appLabel(a).localeCompare(this.appLabel(b), undefined, { sensitivity: 'base' })
      )
    ),
    catchError(() => of([])),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly allPatterns$: Observable<UiJobLogPattern[]> = this.reload$.pipe(
    tap(() => {
      this.loading$.next(true);
      this.error$.next('');
    }),
    switchMap(() =>
      this.patternService.list().pipe(
        map(items => (items ?? []) as UiJobLogPattern[]),
        catchError(() => {
          this.error$.next('Failed to load patterns.');
          return of([] as UiJobLogPattern[]);
        })
      )
    ),
    tap(items => {
      this.latestPatterns = items ?? [];
      this.latestCategoryOptions = this.buildCategoryOptions(this.latestPatterns);
      this.loading$.next(false);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly patterns$: Observable<UiJobLogPattern[]> = combineLatest([
    this.allPatterns$,
    this.q$.pipe(startWith('')),
  ]).pipe(
    map(([patterns, q]) => {
      const filter = (q ?? '').trim().toLowerCase();
      if (!filter) return patterns;

      return patterns.filter(pattern =>
        [pattern.title, pattern.pattern, pattern.category, pattern.description, pattern.displayMessage]
          .some(value => (value ?? '').toLowerCase().includes(filter))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly categoryOptions$: Observable<string[]> = this.allPatterns$.pipe(
    map(patterns => this.buildCategoryOptions(patterns)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  matchText = '';
  onlyMatches = false;

  private readonly matchText$ = new BehaviorSubject<string>('');
  private readonly onlyMatches$ = new BehaviorSubject<boolean>(false);

  userLabel(name?: string | null): string {
    if (name && name.trim()) return name.trim();
    return '—';
  }

  readonly matchingIds$: Observable<Set<number>> = combineLatest([
    this.matchText$.pipe(startWith('')),
    this.reload$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([text]) => {
      const msg = (text ?? '').trim();
      if (!msg) return of(new Set<number>());

      // Use current filter selectors if you want them to influence matching.
      // Here we use "All/no filter" by default.
      return this.patternService.matchPatterns({
        message: msg,
        activeOnly: true,
      }).pipe(
        map(res => new Set<number>((res?.patternIds ?? []).map(x => Number(x)))),
        catchError(() => of(new Set<number>()))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Derive visible patterns from the full list and match filtering.
  readonly visiblePatterns$: Observable<UiJobLogPattern[]> = combineLatest([
    this.patterns$,
    this.matchingIds$,
    this.onlyMatches$.pipe(startWith(false)),
  ]).pipe(
    map(([patterns, matchIds, onlyMatches]) => {
      if (!onlyMatches) return patterns;
      return patterns.filter(p => matchIds.has(p.id));
    })
  );

  editingId: number | null = null;
  form: UpsertJobLogPatternRequest = this.emptyForm();

  constructor(
    private patternService: JobLogPatternService,
    private applicationService: ApplicationService,
    private router: Router,
    private route: ActivatedRoute,
    private ctx: LogMatcherContextService,
    private selectedCompanyService: SelectedCompanyService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(qp => {
      const mode = qp.get('mode');
      const edit = qp.get('edit');

      if (mode === 'new') {
        this.openNewFromContext();
        return;
      }

      if (edit) {
        const id = Number(edit);
        if (!Number.isNaN(id)) {
          this.openEditByIdIfLoaded(id);
        }
      }

      combineLatest([
        this.q$.pipe(startWith('')),
        this.matchText$.pipe(startWith('')),
        this.onlyMatches$.pipe(startWith(false)),
        this.reload$.pipe(startWith(undefined)),
      ]).subscribe(() => this.setPage(1));
    });
  }

  formatUtc(v: string | Date | null | undefined): string {
    if (!v) return '—';

    let d: Date;

    if (v instanceof Date) {
      d = v;
    } else {
      // Force UTC interpretation
      const iso = v.endsWith('Z') ? v : v.replace(' ', 'T') + 'Z';
      d = new Date(iso);
    }

    if (Number.isNaN(d.getTime())) return '—';

    const pad = (n: number) => n.toString().padStart(2, '0');

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  get isCustomDateRange(): boolean {
    return this.datePreset === 'custom';
  }

  onDateRangeChange() {
    this.expandedMatchPatternIds.clear();
    this.patternMatches.clear();
    this.patternMatchErrors.clear();
  }

  hasValidDateRange(): boolean {
    return this.resolveDateRange() !== null;
  }

  hasPreviewArticles(p: UiJobLogPattern): boolean {
    return this.getPreviewArticleIdsFromPattern(p).length > 0;
  }

  private getPreviewArticleIdsFromPattern(p: UiJobLogPattern): number[] {
    const ids =
      (p.freshdeskArticleIds ?? p.freshdeskArticleRowIds ?? []) as number[];

    return Array.from(
      new Set(
        (ids ?? [])
          .map(x => Number(x))
          .filter(x => Number.isFinite(x) && x > 0)
      )
    );
  }

  showPreview(p: UiJobLogPattern): void {
    const ids = this.getPreviewArticleIdsFromPattern(p);
    if (!ids.length) return;

    const origin = {
      kind: 'pattern_admin' as const,
      pattern: {
        id: p.id ?? null,
        title: p.title ?? null,
      },
      logMessage: p.pattern ?? null,
    };

    this.helpModal?.open(
      `Preview help articles${p.title ? ` • ${p.title}` : ''}`,
      ids,
      origin
    );
  }

  showPreviewFromForm(): void {
    const ids = this.parseArticleIds(this.articleIdsText);
    if (!ids.length) return;

    const origin = {
      kind: 'pattern_admin' as const,
      pattern: {
        id: this.editingId ?? null,
        title: this.form.title ?? null,
      },
      logMessage: this.form.pattern ?? null,
    };

    this.helpModal?.open(
      `Preview help articles${this.form.title ? ` • ${this.form.title}` : ''}`,
      ids,
      origin
    );
  }

  page = 1;
  pageSize = 25;

  private readonly page$ = new BehaviorSubject<number>(1);
  private readonly pageSize$ = new BehaviorSubject<number>(25);

  readonly pageSizeOptions = [10, 25, 50, 100] as const;

  setPage(p: number) {
    const next = Math.max(1, Math.floor(p || 1));
    this.page = next;
    this.page$.next(next);
  }

  setPageSize(sz: number) {
    const next = Math.max(1, Math.floor(sz || 25));
    this.pageSize = next;
    this.pageSize$.next(next);
    this.setPage(1); // reset to first page
  }

  /** How many pages to show around the current page */
  private readonly pagerWindow = 2;

  private buildPager(current: number, total: number, window: number): PagerToken[] {
    if (total <= 1) return [{ kind: 'page', value: 1, isCurrent: true }];

    const clamp = (n: number) => Math.min(Math.max(1, n), total);
    const cur = clamp(current);

    const pages = new Set<number>();
    pages.add(1);
    pages.add(total);

    for (let i = cur - window; i <= cur + window; i++) {
      if (i >= 1 && i <= total) pages.add(i);
    }

    // When close to ends, pull in extra pages so the pager feels stable
    if (cur <= 1 + window) {
      for (let i = 1; i <= Math.min(total, 1 + window * 2 + 1); i++) pages.add(i);
    }
    if (cur >= total - window) {
      for (let i = Math.max(1, total - (window * 2 + 1)); i <= total; i++) pages.add(i);
    }

    const sorted = Array.from(pages).sort((a, b) => a - b);

    const tokens: PagerToken[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const v = sorted[i];
      const prev = sorted[i - 1];

      if (i > 0 && prev !== undefined && v - prev > 1) {
        tokens.push({ kind: 'gap' });
      }

      tokens.push({ kind: 'page', value: v, isCurrent: v === cur });
    }

    return tokens;
  }

  readonly filteredCount$ = this.visiblePatterns$.pipe(
    map(items => items.length),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly totalPages$ = combineLatest([
    this.filteredCount$,
    this.pageSize$.pipe(startWith(this.pageSize)),
  ]).pipe(
    map(([count, size]) => Math.max(1, Math.ceil(count / Math.max(1, size)))),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  readonly currentPage$ = combineLatest([
    this.page$.pipe(startWith(this.page)),
    this.totalPages$,
  ]).pipe(
    map(([page, total]) => Math.min(Math.max(1, page), total)),
    tap(p => {
      // keep "page" field in sync for template bindings
      if (this.page !== p) this.page = p;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly pagerTokens$ = combineLatest([
    this.currentPage$,
    this.totalPages$,
  ]).pipe(
    map(([cur, total]) => this.buildPager(cur, total, this.pagerWindow)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly pagedPatterns$ = combineLatest([
    this.visiblePatterns$,
    this.currentPage$,
    this.pageSize$.pipe(startWith(this.pageSize)),
  ]).pipe(
    map(([items, page, size]) => {
      const s = Math.max(1, size);
      const start = (page - 1) * s;
      return items.slice(start, start + s);
    })
  );



  onMatchTextChange(v: string) {
    this.matchText = v ?? '';
    this.matchText$.next(this.matchText);
  }

  toggleOnlyMatches(v: boolean) {
    this.onlyMatches = !!v;
    this.onlyMatches$.next(this.onlyMatches);
  }

  // ✅ used in template
  jobLogTypeLabel(v: number | null | undefined): string {
    // legacy safety: backend might still return 5
    if (v === 5 || v === null || v === undefined) return 'All';
    switch (v) {
      case JobLogType.Error:
        return 'Error';
      case JobLogType.Fail:
        return 'Fail';
      case JobLogType.Info:
        return 'Info';
      case JobLogType.Success:
        return 'Success';
      case JobLogType.AmountMismatch:
        return 'Amount mismatch';
      case JobLogType.Undefined:
        return 'Undefined';
      default:
        return `#${v}`;
    }
  }

  // Template helper
  linkIdOf(p: UiJobLogPattern): number | null {
    const v = p.linkId;
    return typeof v === 'number' ? v : null;
  }

  onFilterChange(v: string) {
    this.q = v ?? '';
    this.q$.next(this.q);
  }

  refresh() {
    this.reload$.next(undefined);
  }

  async toggleMatchedLogs(pattern: UiJobLogPattern) {
    if (this.expandedMatchPatternIds.has(pattern.id)) {
      this.expandedMatchPatternIds.delete(pattern.id);
      return;
    }

    const range = this.resolveDateRange();
    if (!range) {
      Swal.fire({
        icon: 'error',
        title: 'Date range required',
        text: 'Select both a from date and a to date.',
      });
      return;
    }

    this.expandedMatchPatternIds.add(pattern.id);
    this.loadingMatchPatternIds.add(pattern.id);
    this.patternMatchErrors.delete(pattern.id);

    try {
      const response = await firstValueFrom(this.patternService.getMatches(pattern.id, {
        fromUtc: range.fromUtc,
        toUtc: range.toUtc,
        take: 25,
      }));
      this.patternMatches.set(pattern.id, response.jobLogs ?? []);
    } catch (error) {
      this.patternMatches.set(pattern.id, []);
      this.patternMatchErrors.set(pattern.id, 'Failed to load matched logs.');
      showErrorSwal(error);
    } finally {
      this.loadingMatchPatternIds.delete(pattern.id);
      this.changeDetectorRef.markForCheck();
    }
  }

  async openMatchedJobLog(jobLog: JobLogPatternMatchDto, event?: Event) {
    event?.stopPropagation();

    const switchedCompany = await this.selectedCompanyService.setCompanyId(jobLog.companyId);
    if (!switchedCompany) {
      this.error$.next('Failed to switch to the selected log company.');
      return;
    }

    await this.router.navigate(['application-link/link/' + jobLog.linkId], {
      queryParams: {
        tab: 4,
        instanceId: jobLog.instanceId ?? null,
        jobLogId: jobLog.id,
      },
    });
  }

  groupMatchedLogsByCustomer(jobLogs: JobLogPatternMatchDto[]): CustomerJobLogGroup[] {
    const groups = new Map<number, CustomerJobLogGroup>();

    for (const jobLog of jobLogs) {
      const group = groups.get(jobLog.companyId);
      if (group) {
        group.jobLogs.push(jobLog);
      } else {
        groups.set(jobLog.companyId, {
          companyId: jobLog.companyId,
          companyName: jobLog.companyName,
          jobLogs: [jobLog],
        });
      }
    }

    return Array.from(groups.values());
  }

  exportCsv(patterns: UiJobLogPattern[], applications: Application[]) {
    if (!patterns?.length) return;

    const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = [
      'Title',
      'Pattern',
      'Description',
      'Category',
      'Display message',
      'Type',
      'From',
      'To',
      'Article numbers',
      'Created by',
      'Created',
      'Last edited by',
      'Updated',
      'Status',
    ];

    const lines = patterns.map(pattern => [
      pattern.title,
      pattern.pattern,
      pattern.description,
      pattern.category,
      pattern.displayMessage,
      this.jobLogTypeLabel(pattern.jobLogType),
      this.csvApplicationName(applications, pattern.fromApplicationId),
      this.csvApplicationName(applications, pattern.toApplicationId),
      this.csvArticleNumbers(pattern.freshdeskArticleRowIds),
      pattern.createdByName,
      pattern.createdUtc,
      pattern.updatedByName,
      pattern.updatedUtc,
      pattern.status === 1 ? 'Active' : 'Inactive',
    ].map(escapeCsv).join(','));

    const csv = String.fromCharCode(0xfeff) + [header.map(escapeCsv).join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = `logmatcher-patterns-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
  }

  newPattern() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: 'new', edit: null },
      queryParamsHandling: 'merge',
    });
  }

  refreshAllArticles(p: UiJobLogPattern) {
    if (!p?.id) return;

    this.loading$.next(true);
    this.error$.next('');

    this.patternService.refreshArticles(p.id)
      .pipe(
        catchError((err) => {
          this.loading$.next(false);
          showErrorSwal(err);
          return of(null);
        })
      )
      .subscribe(result => {
        this.loading$.next(false);
        if (!result) return;

        Swal.fire({
          icon: 'success',
          title: 'Articles refreshed',
          text: result.refreshedArticleIds?.length
            ? `${result.refreshedArticleIds.length} article(s) refreshed.`
            : 'No linked articles found.',
          timer: 1600,
          showConfirmButton: false,
        });

        this.refresh();
      });
  }

  refreshSingleArticle(p: UiJobLogPattern, articleId: number) {
    if (!p?.id || !articleId) return;

    this.loading$.next(true);
    this.error$.next('');

    this.patternService.refreshArticle(p.id, articleId)
      .pipe(
        catchError((err) => {
          this.loading$.next(false);
          showErrorSwal(err);
          return of(null);
        })
      )
      .subscribe(result => {
        this.loading$.next(false);
        if (!result) return;

        Swal.fire({
          icon: 'success',
          title: 'Article refreshed',
          text: `Article ${articleId} refreshed.`,
          timer: 1400,
          showConfirmButton: false,
        });

        this.refresh();
      });
  }



  private openNewFromContext() {
    const draft = this.ctx.draft$.value;
    const f = this.ctx.filters$.value;

    this.mode = 'edit';
    this.editingId = null;
    this.error$.next('');

    // ✅ normalize legacy "5" to null
    const normalizeJobLogType = (x: number | null | undefined) => (x === 5 ? null : x ?? null);

    this.form = {
      ...this.emptyForm(),
      title: draft.title ?? '',
      pattern: draft.pattern ?? '',
      category: draft.category ?? null,
      status: (draft.status ?? 1) as PatternStatus,

      linkId: draft.linkId ?? f.linkId ?? null,
      jobLogType: normalizeJobLogType(draft.jobLogType ?? f.jobLogType),
      fromApplicationId: draft.fromApplicationId ?? f.fromApplicationId ?? null,
      toApplicationId: draft.toApplicationId ?? f.toApplicationId ?? null,
      linkTypeId: draft.linkTypeId ?? null,

      description: (draft.description ?? '') as any,
      displayMessage: (draft.displayMessage ?? '') as any,
      freshdeskArticleIds: (draft.freshdeskArticleIds ?? []) as any,
      searchInStackTrace: (draft.searchInStackTrace)
    };

    this.setCategorySelection(this.form.category);
    this.articleIdsText = (this.form.freshdeskArticleIds ?? []).join(', ');
  }

  private openEditByIdIfLoaded(id: number) {
    const found = this.latestPatterns.find(x => x.id === id);
    if (found) this.edit(found);
  }

  edit(p: UiJobLogPattern) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: p.id, mode: null },
      queryParamsHandling: 'merge',
    });

    this.mode = 'edit';
    this.editingId = p.id;
    this.error$.next('');

    const rowIds = ((p as any).freshdeskArticleRowIds ?? []) as number[];

    const normalizeJobLogType = (x: number | null | undefined) => (x === 5 ? null : x ?? null);

    this.form = {
      title: p.title ?? '',
      pattern: p.pattern ?? '',
      status: (p.status ?? 1) as PatternStatus,
      category: p.category ?? null,

      linkId: this.linkIdOf(p),
      jobLogType: normalizeJobLogType(p.jobLogType),

      fromApplicationId: p.fromApplicationId ?? null,
      toApplicationId: p.toApplicationId ?? null,
      linkTypeId: p.linkTypeId ?? null,

      description: (p as any).description ?? null,
      displayMessage: (p as any).displayMessage ?? null,
      searchInStackTrace: (p as any).searchInStackTrace,

      // keep sending external article IDs on save (your API expects FreshdeskArticleIds),
      // but for now just store row ids here so the field is filled:
      freshdeskArticleIds: rowIds as any,
    };

    this.setCategorySelection(this.form.category);
    // ✅ this is what fills the input
    this.articleIdsText = rowIds.join(', ');

  }

  cancel() {
    this.mode = 'list';
    this.editingId = null;
    this.form = this.emptyForm();
    this.categorySelection = '';
    this.articleIdsText = '';
    this.error$.next('');

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: null, edit: null },
      queryParamsHandling: 'merge',
    });
  }

  parseArticleIds(input: string): number[] {
    return Array.from(
      new Set(
        (input ?? '')
          .split(/[\s,;]+/g)
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => Number(s))
          .filter(n => Number.isFinite(n) && n > 0)
      )
    );
  }

  onCategorySelectionChange(value: string) {
    this.categorySelection = value;
    this.form.category = value === this.createCategoryOption ? '' : value || null;
  }

  save() {
    this.error$.next('');

    const title = (this.form.title ?? '').trim();
    const pattern = (this.form.pattern ?? '').trim();
    let category = (this.form.category ?? '').trim();

    if (!title) {
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: 'Title is required.',
      });
      return;
    }

    if (!pattern) {
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: 'Pattern is required.',
      });
      return;
    }

    if (this.categorySelection === this.createCategoryOption && !category) {
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: 'Enter a name for the new category.',
      });
      return;
    }

    const existingCategory = this.latestCategoryOptions.find(option =>
      option.toLowerCase() === category.toLowerCase()
    );
    category = existingCategory ?? category;

    const normalizedJobLogType =
      this.form.jobLogType === 5 ? null : this.form.jobLogType;

    const payload: UpsertJobLogPatternRequest = {
      ...this.form,
      title,
      pattern,
      category: category || null,
      jobLogType: normalizedJobLogType,
      status: (this.form.status ?? 1) as PatternStatus,
      description: (this.form.description ?? '').trim() || null,
      displayMessage: (this.form.displayMessage ?? '').trim() || null,
      freshdeskArticleIds: this.parseArticleIds(this.articleIdsText),
    };

    this.loading$.next(true);

    const req$ = this.editingId
      ? this.patternService.update(this.editingId, payload)
      : this.patternService.create(payload);

    req$
      .pipe(
        catchError((err) => {
          this.loading$.next(false);

          const message =
            err?.error?.message ||
            err?.error ||
            'An unexpected error occurred while saving.';

          showErrorSwal(err);

          return of(null);
        })
      )
      .subscribe(result => {
        this.loading$.next(false);

        if (!result) return;

        Swal.fire({
          icon: 'success',
          title: this.editingId ? 'Pattern updated' : 'Pattern created',
          timer: 1400,
          showConfirmButton: false,
        });

        this.cancel();
        this.refresh();
      });
  }

  toggleStatus(p: UiJobLogPattern) {
    const next: PatternStatus = p.status === 1 ? 0 : 1;

    this.loading$.next(true);
    this.patternService
      .setStatus(p.id, next)
      .pipe(
        catchError(() => {
          this.error$.next('Failed to update status.');
          return of(void 0);
        }),
        tap(() => this.loading$.next(false))
      )
      .subscribe(() => this.refresh());
  }

  test(p: UiJobLogPattern) {
    this.ctx.draft$.next({
      title: p.title ?? '',
      pattern: p.pattern ?? '',
      category: p.category ?? null,
      status: (p.status ?? 1) as PatternStatus,

      linkId: this.linkIdOf(p),
      jobLogType: (p.jobLogType === 5 ? null : p.jobLogType) ?? null,
      fromApplicationId: p.fromApplicationId ?? null,
      toApplicationId: p.toApplicationId ?? null,
      linkTypeId: p.linkTypeId ?? null,

      description: (p as any).description ?? null,
      displayMessage: (p as any).displayMessage ?? null,
      freshdeskArticleIds: ((p as any).freshdeskArticleIds ?? []) as number[],
      searchInStackTrace: p.searchInStackTrace
    });

    this.router.navigate(['../analysis'], {
      relativeTo: this.route,
      queryParams: { patternId: p.id },
    });
  }

  async confirmDelete(p: UiJobLogPattern) {
    const result = await Swal.fire({
      title: 'Delete pattern?',
      text: `Are you sure you want to delete "${p.title}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.deletePattern(p.id);
  }


  async confirmDeleteById(id: number) {
    const p = this.latestPatterns.find(x => x.id === id);
    const title = p?.title ?? `#${id}`;

    const result = await Swal.fire({
      title: 'Delete pattern?',
      text: `Are you sure you want to delete "${title}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.deletePattern(id);
  }

  private deletePattern(id: number) {
    this.error$.next('');
    this.loading$.next(true);

    this.patternService
      .delete(id)
      .pipe(
        catchError(() => {
          this.error$.next('Delete failed.');
          return of(void 0);
        }),
        tap(() => this.loading$.next(false))
      )
      .subscribe(() => {
        // If user deleted while editing the same pattern, exit edit mode
        if (this.editingId === id) {
          this.cancel();
        }
        this.refresh();
      });
  }

  private emptyForm(): UpsertJobLogPatternRequest {
    return {
      title: '',
      pattern: '',
      status: 1,
      category: null,

      linkId: null,
      jobLogType: null,

      fromApplicationId: null,
      toApplicationId: null,
      linkTypeId: null,

      description: null as any,
      displayMessage: null as any,
      freshdeskArticleIds: [] as any,
      searchInStackTrace: false
    };
  }

  appLabel(a: Application): string {
    return a.displayName || a.name || '';
  }

  appNameById(apps: Application[], id: number | null | undefined): string {
    if (!id) return '';
    const found = apps.find(x => x.id === id);
    return found ? this.appLabel(found) : `#${id}`;
  }

  private csvApplicationName(apps: Application[], id: number | null | undefined): string {
    return id ? this.appNameById(apps, id) : 'All';
  }

  private csvArticleNumbers(articleNumbers: number[] | null | undefined): string {
    const value = (articleNumbers ?? []).join('; ');
    return value ? `="${value}"` : '';
  }

  private setCategorySelection(category: string | null | undefined) {
    const normalizedCategory = (category ?? '').trim();
    if (!normalizedCategory) {
      this.categorySelection = '';
      return;
    }

    const existingCategory = this.latestCategoryOptions.find(option =>
      option.toLowerCase() === normalizedCategory.toLowerCase()
    );

    this.categorySelection = existingCategory ?? this.createCategoryOption;
    this.form.category = existingCategory ?? normalizedCategory;
  }

  private buildCategoryOptions(patterns: UiJobLogPattern[]): string[] {
    const categories = new Map<string, string>();

    for (const pattern of patterns) {
      const category = (pattern.category ?? '').trim();
      if (category && !categories.has(category.toLowerCase())) {
        categories.set(category.toLowerCase(), category);
      }
    }

    return Array.from(categories.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }

  private resolveDateRange(): { fromUtc: string; toUtc: string } | null {
    if (this.datePreset === 'custom') {
      if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) return null;

      return {
        fromUtc: `${this.fromDate}T00:00:00.000Z`,
        toUtc: `${this.toDate}T23:59:59.999Z`,
      };
    }

    const now = new Date();
    const end = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
      999
    ));
    const days = this.datePreset === 'last-7-days' ? 7 : this.datePreset === 'last-30-days' ? 30 : 14;
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - (days - 1));
    start.setUTCHours(0, 0, 0, 0);

    return {
      fromUtc: start.toISOString(),
      toUtc: end.toISOString(),
    };
  }

  trackById(_: number, item: UiJobLogPattern) {
    return item.id;
  }

  trackByJobLogId(_: number, jobLog: JobLogPatternMatchDto) {
    return jobLog.id;
  }

  trackByCompanyId(_: number, group: CustomerJobLogGroup) {
    return group.companyId;
  }
}
